import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, extractError } from "../lib/api";
import {
  canEditFinalizedUnloadLines,
  canEditUnloadDestinations,
  contractAllocLabel,
  isUnloadDestinationFinalized,
  purchaseDistinctQualities,
  purchaseHasFinalizedAccounts,
  purchaseRequiresUnloaderContractPick,
} from "../lib/unloadingHelpers";
import { useAuth, hasPermission, isAdmin } from "../context/AuthContext";
import { useLookups } from "../context/LookupOptionsContext";
import { SectionCard, Pill } from "./Ui";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Pencil, Trash2, Lock, Split } from "lucide-react";
import LabourSurchargeTierFields from "./LabourSurchargeTierFields";
import LabourContractorPicker from "./LabourContractorPicker";
import ProductionBatchSelect from "./ProductionBatchSelect";
import { labourTierLabel } from "../lib/labourConditionTiers";
import { todayIsoDate } from "../lib/purchaseListMeta";

const HOUDIS = ["H-1", "H-2", "H-3", "H-4"];

function roundQtl(n) {
  return Math.round(Number(n || 0) * 1000) / 1000;
}

function destToEditRow(d, index) {
  return {
    index,
    location_type: d.location_type || "HOUDI",
    location_id: d.location_id || "H-1",
    variety: d.variety || "",
    bags: d.bags ?? 0,
    purchase_contract_id: d.purchase_contract_id != null ? String(d.purchase_contract_id) : "",
    production_batch_id: d.production_batch_id != null ? String(d.production_batch_id) : "",
    production_batch_code: d.production_batch_code || "",
    isNew: false,
    splitFromIndex: null,
    finalized: isUnloadDestinationFinalized(d),
    weight_qtl: d.weight_qtl,
    batch_id: d.batch_id,
    stack_height_tier: d.stack_height_tier || "BASE",
    carry_distance_tier: d.carry_distance_tier || "BASE",
  };
}

export default function RecordedUnloadEditor({
  purchase,
  stacks = [],
  productionBatches = [],
  conditionRules: conditionRulesProp,
  labourContractorName = "",
  embedContractorPicker = false,
  onSaved,
  adminOnly = false,
}) {
  const { user } = useAuth();
  const { optionsFor } = useLookups();
  const varieties = optionsFor("paddy_variety");
  const adminUser = isAdmin(user);
  const canEditState = canEditUnloadDestinations(purchase, user);
  const canEdit = adminOnly
    ? (adminUser || hasPermission(user, "unloading.create")) && canEditState
    : hasPermission(user, "unloading.create") && canEditState;
  const adminFinalizedEdit = canEditFinalizedUnloadLines(user, purchase) && canEdit;
  const willReopenVoucher = purchaseHasFinalizedAccounts(purchase);
  const unloadByQtl = Boolean(purchase?.unload_by_qtl);
  const contractAllocs = purchase?.contract_allocations || [];
  const truckQuality = purchaseDistinctQualities(purchase)[0] || purchase?.variety || "";
  const requiresContractPick = purchaseRequiresUnloaderContractPick(purchase);
  const recordedDests = purchase?.unloading?.destinations || [];

  const initialRows = useMemo(
    () => recordedDests.map((d, i) => destToEditRow(d, i)),
    [recordedDests],
  );

  const [rows, setRows] = useState(initialRows);
  const [removed, setRemoved] = useState([]);
  const [saving, setSaving] = useState(false);
  const [conditionRules, setConditionRules] = useState(conditionRulesProp || null);
  const nextNewIndex = useRef(-1);

  useEffect(() => {
    if (conditionRulesProp) {
      setConditionRules(conditionRulesProp);
      return;
    }
    const partyId = purchase?.unloading?.labour_contractor_party_id;
    if (!partyId) {
      setConditionRules(null);
      return;
    }
    api
      .get("/labour-contracts/active", {
        params: {
          party_id: partyId,
          work_date: todayIsoDate(),
        },
      })
      .then((r) => setConditionRules(r.data?.active ? r.data.condition_rules || null : null))
      .catch(() => setConditionRules(null));
  }, [conditionRulesProp, purchase?.id, purchase?.unloading?.labour_contractor_party_id]);

  useEffect(() => {
    setRows(initialRows);
    setRemoved([]);
    nextNewIndex.current = -1;
  }, [initialRows]);

  if (!recordedDests.length) return null;

  const canEditBatch = adminUser && canEdit;
  const editableRows = rows.filter((r) => r.isNew || !r.finalized || adminFinalizedEdit);
  const hasEditable = editableRows.length > 0;

  const rowIsEditable = (r) => canEdit && (r.isNew || !r.finalized || adminFinalizedEdit);

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((r) => (r.index === index ? { ...r, ...patch } : r)));
  };

  const removeRow = (index) => {
    const row = rows.find((r) => r.index === index);
    if (!row) return;
    if (row.isNew) {
      setRows((prev) => {
        const next = prev.filter((r) => r.index !== index);
        if (unloadByQtl || row.splitFromIndex == null) return next;
        return next.map((r) => (
          r.index === row.splitFromIndex
            ? { ...r, bags: Number(r.bags || 0) + Number(row.bags || 0) }
            : r
        ));
      });
      return;
    }
    if (row.finalized && !adminFinalizedEdit) return;
    if (row.finalized) return;
    setRemoved((prev) => [...prev, index]);
    setRows((prev) => prev.filter((r) => r.index !== index));
  };

  const splitRow = (index) => {
    if (!canEditBatch) return;
    setRows((prev) => {
      const row = prev.find((r) => r.index === index);
      if (!row || row.isNew) return prev;
      if (unloadByQtl) {
        const qtl = Number(row.weight_qtl) || 0;
        if (qtl <= 0) {
          toast.error("Weigh this line before splitting qtl onto another mill batch");
          return prev;
        }
        const moveQtl = Math.round((qtl / 2) * 1000) / 1000;
        if (moveQtl <= 0 || qtl - moveQtl <= 0) {
          toast.error("Split must leave qtl on the original line");
          return prev;
        }
        const newIndex = nextNewIndex.current--;
        return [
          ...prev.map((r) => (r.index === index ? { ...r, weight_qtl: roundQtl(qtl - moveQtl) } : r)),
          {
            ...row,
            index: newIndex,
            isNew: true,
            splitFromIndex: row.index,
            bags: 0,
            weight_qtl: moveQtl,
            production_batch_id: "",
            production_batch_code: "",
          },
        ];
      }
      const bags = Number(row.bags) || 0;
      if (bags < 2) {
        toast.error("Need at least 2 bags to split onto another mill batch");
        return prev;
      }
      const moveBags = Math.max(1, Math.floor(bags / 2));
      const leftover = bags - moveBags;
      if (leftover <= 0) {
        toast.error("Split must leave bags on the original line");
        return prev;
      }
      const newIndex = nextNewIndex.current--;
      return [
        ...prev.map((r) => (r.index === index ? { ...r, bags: leftover } : r)),
        {
          ...row,
          index: newIndex,
          isNew: true,
          splitFromIndex: row.index,
          bags: moveBags,
          production_batch_id: "",
          production_batch_code: "",
        },
      ];
    });
  };

  const save = async () => {
    const existing = rows.filter((r) => !r.isNew && rowIsEditable(r));
    const updates = existing.map((r) => {
      const row = {
        index: r.index,
        location_type: r.location_type,
        location_id: r.location_id,
        variety: r.variety || undefined,
        bags: unloadByQtl ? 0 : Number(r.bags) || 0,
        purchase_contract_id: r.purchase_contract_id ? Number(r.purchase_contract_id) : undefined,
      };
      if (canEditBatch && r.production_batch_id) {
        row.production_batch_id = Number(r.production_batch_id);
      }
      row.stack_height_tier = r.stack_height_tier || "BASE";
      row.carry_distance_tier = r.carry_distance_tier || "BASE";
      return row;
    });
    const additions = rows
      .filter((r) => r.isNew)
      .map((r) => ({
        split_from_index: r.splitFromIndex,
        location_type: r.location_type,
        location_id: r.location_id,
        variety: r.variety || undefined,
        bags: unloadByQtl ? 0 : Number(r.bags) || 0,
        weight_qtl: unloadByQtl ? Number(r.weight_qtl) || 0 : undefined,
        purchase_contract_id: r.purchase_contract_id ? Number(r.purchase_contract_id) : undefined,
        production_batch_id: r.production_batch_id ? Number(r.production_batch_id) : undefined,
        stack_height_tier: r.stack_height_tier || "BASE",
        carry_distance_tier: r.carry_distance_tier || "BASE",
      }));
    if (!unloadByQtl && updates.some((u) => !u.bags || u.bags <= 0)) {
      toast.error("Enter bags for each editable line");
      return;
    }
    if (!unloadByQtl && additions.some((u) => !u.bags || u.bags <= 0)) {
      toast.error("Enter bags for each split line");
      return;
    }
    if (requiresContractPick && [...updates, ...additions].some((u) => !u.purchase_contract_id)) {
      toast.error("Select quality / contract for each editable line");
      return;
    }
    if (additions.some((u) => u.split_from_index == null || u.split_from_index < 0)) {
      toast.error("Split line is missing its source unload line");
      return;
    }
    if (additions.some((u) => !u.production_batch_id)) {
      toast.error("Select a mill batch for each split line");
      return;
    }
    try {
      setSaving(true);
      const { data } = await api.patch(`/unloading/purchase/${purchase.id}/destinations`, {
        updates,
        remove_indices: removed,
        additions,
      });
      toast.success(additions.length ? "Unload split across mill batches" : "Unloading updated");
      if (data?.sync_warning) {
        toast.warning(`Unloading saved — labour sync: ${data.sync_warning}`);
      }
      setRemoved([]);
      onSaved?.();
    } catch (err) {
      const message = extractError(err);
      const isNetwork = !err?.response && (message === "Network Error" || err?.code === "ERR_NETWORK");
      toast.error(isNetwork ? "Could not reach server — check connection and retry" : message);
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    removed.length > 0
    || rows.some((r) => r.isNew)
    || rows.some((r) => {
      if (r.isNew || !rowIsEditable(r)) return false;
      const orig = initialRows.find((o) => o.index === r.index);
      if (!orig) return true;
      return (
        r.location_type !== orig.location_type
        || r.location_id !== orig.location_id
        || r.variety !== orig.variety
        || String(r.bags) !== String(orig.bags)
        || String(r.purchase_contract_id) !== String(orig.purchase_contract_id)
        || String(r.production_batch_id || "") !== String(orig.production_batch_id || "")
        || String(r.carry_distance_tier) !== String(orig.carry_distance_tier)
        || String(r.stack_height_tier) !== String(orig.stack_height_tier)
      );
    });

  const contractorLabel =
    labourContractorName || purchase?.unloading?.labour_contractor_name || "";

  return (
    <SectionCard title="Recorded unloading" testId="recorded-unload-editor">
      {embedContractorPicker && (
        <LabourContractorPicker
          purchaseId={purchase.id}
          unloading={purchase.unloading}
          onSaved={() => onSaved?.()}
        />
      )}
      {!embedContractorPicker && (
        contractorLabel ? (
          <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
            <span className="text-muted-foreground">Labour contractor</span>
            <Pill tone="sage">{contractorLabel}</Pill>
            <span className="text-[10px] text-muted-foreground">
              Change contractor using the picker above this section.
            </span>
          </div>
        ) : (
          <p className="text-xs text-amber-700 mb-3">
            No labour contractor assigned yet — select one above before mid weigh or accounts settlement.
          </p>
        )
      )}
      <p className="text-xs text-muted-foreground mb-3">
        {canEditBatch
          ? "Admin: change mill batch on a line, or Split line to move bags onto another mill batch after the unloader recorded a single batch."
          : canEdit && hasEditable
          ? adminFinalizedEdit && willReopenVoucher
            ? "Edit bags, location, or contract on weighed lines. Saving moves the finalized voucher back to accounts pending."
            : adminFinalizedEdit
              ? "Edit bags, location, or contract — including lines already weighed at bridge."
              : "Edit location, bags, or contract on lines not yet weighed. Finalized lines (weight applied) are locked."
          : canEdit
            ? "No editable unloading lines on this purchase."
            : "You cannot edit unloading on this purchase in its current state."}
      </p>
      <div className="space-y-3">
        {rows.map((r) => (
          <div
            key={r.index}
            className={`rounded-xl border p-3 ${r.finalized ? "border-border bg-muted/20" : "border-bansal-sage/30 bg-card"}`}
            data-testid={`recorded-unload-${r.index}`}
          >
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {r.isNew ? "Split line" : `Line ${r.index + 1}`}
                </span>
                {r.finalized && adminFinalizedEdit ? (
                  <span className="inline-flex items-center gap-1 text-[10px] text-amber-700">
                    <Pencil className="h-3 w-3" /> Weighed — editable
                  </span>
                ) : r.finalized ? (
                  <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                    <Lock className="h-3 w-3" /> Finalized
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] text-primary">
                    <Pencil className="h-3 w-3" /> Editable
                  </span>
                )}
                {Number(r.weight_qtl) > 0 && (
                  <span className="text-xs font-medium text-primary tabular">{r.weight_qtl} qtl</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {canEditBatch && !r.isNew && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-amber-800"
                    onClick={() => splitRow(r.index)}
                    data-testid={`split-unload-${r.index}`}
                  >
                    <Split className="h-3.5 w-3.5 mr-1" /> Split
                  </Button>
                )}
                {canEdit && (r.isNew || !r.finalized) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600 h-8"
                    onClick={() => removeRow(r.index)}
                    data-testid={`remove-unload-${r.index}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>

            {rowIsEditable(r) ? (
              <>
                <div className={`grid grid-cols-1 gap-3 ${unloadByQtl ? "sm:grid-cols-3" : "sm:grid-cols-4"}`}>
                  <Field label="Type">
                    <Select value={r.location_type} onValueChange={(v) => updateRow(r.index, { location_type: v, location_id: v === "HOUDI" ? "H-1" : stacks[0]?.stack_id })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HOUDI">Houdi</SelectItem>
                        <SelectItem value="STACK">Stack</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Location">
                    <Select value={r.location_id} onValueChange={(v) => updateRow(r.index, { location_id: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(r.location_type === "HOUDI" ? HOUDIS : stacks.map((s) => s.stack_id)).map((id) => (
                          <SelectItem key={id} value={id}>{id}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  {requiresContractPick ? (
                    <Field label="Quality / contract">
                      <Select
                        value={r.purchase_contract_id}
                        onValueChange={(v) => {
                          const alloc = contractAllocs.find((a) => String(a.purchase_contract_id) === String(v));
                          updateRow(r.index, {
                            purchase_contract_id: v,
                            variety: alloc?.variety || r.variety,
                          });
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder="Contract…" /></SelectTrigger>
                        <SelectContent>
                          {contractAllocs.map((a) => (
                            <SelectItem key={a.purchase_contract_id} value={String(a.purchase_contract_id)}>
                              {contractAllocLabel(a)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  ) : truckQuality ? (
                    <Field label="Quality">
                      <Input value={truckQuality} readOnly className="bg-muted/40" />
                    </Field>
                  ) : (
                    <Field label="Variety">
                      <Select value={r.variety} onValueChange={(v) => updateRow(r.index, { variety: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {varieties.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  {!unloadByQtl && (
                    <Field label="Bags">
                      <Input
                        type="number"
                        min="0"
                        value={r.bags}
                        onChange={(e) => updateRow(r.index, { bags: e.target.value })}
                      />
                    </Field>
                  )}
                </div>
                {canEditBatch && (
                <div className="mt-3">
                  <Field label={r.isNew ? "Move onto mill batch" : "Production batch"}>
                    <ProductionBatchSelect
                      batches={productionBatches}
                      value={r.production_batch_id}
                      currentCode={r.production_batch_code}
                      onChange={(v) => updateRow(r.index, { production_batch_id: v })}
                      testId={`recorded-unload-batch-${r.index}`}
                    />
                  </Field>
                </div>
                )}
                {conditionRules && (
                  <LabourSurchargeTierFields
                    conditionRules={conditionRules}
                    locationType={r.location_type}
                    stackHeightTier={r.stack_height_tier}
                    carryDistanceTier={r.carry_distance_tier}
                    onStackHeightTierChange={(v) => updateRow(r.index, { stack_height_tier: v })}
                    onCarryDistanceTierChange={(v) => updateRow(r.index, { carry_distance_tier: v })}
                  />
                )}
              </>
            ) : (
              <div className="flex flex-wrap gap-2 text-sm">
                <Pill tone={r.location_type === "HOUDI" ? "gold" : "sage"}>{r.location_type}</Pill>
                <span className="font-mono">{r.location_id}</span>
                {r.variety && <span className="text-muted-foreground">· {r.variety}</span>}
                {!unloadByQtl && r.bags > 0 && <span>· {r.bags} bags</span>}
                {r.production_batch_code && (
                  <span className="font-mono text-muted-foreground">· {r.production_batch_code}</span>
                )}
                {conditionRules && r.carry_distance_tier && r.carry_distance_tier !== "BASE" && (
                  <span className="text-muted-foreground">
                    · {labourTierLabel(r.carry_distance_tier, conditionRules.distance_tiers)}
                  </span>
                )}
                {r.location_type === "STACK" && conditionRules && r.stack_height_tier && r.stack_height_tier !== "BASE" && (
                  <span className="text-muted-foreground">
                    · {labourTierLabel(r.stack_height_tier, conditionRules.height_tiers)}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {canEdit && hasEditable && (
        <div className="mt-4 flex justify-end">
          <Button
            type="button"
            variant="outline"
            disabled={!dirty || saving}
            onClick={save}
            className="min-h-11"
            data-testid="save-recorded-unload"
          >
            {saving ? "Saving…" : "Save unloading changes"}
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
